import { Link } from "react-router-dom";
import { fileUrl, api } from "../lib/api";
import { CATEGORIES, TYPE_LABELS } from "../lib/categories";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { MapPin, Camera, Gavel, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ListingCard({ listing, onDeleted }) {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const cat = CATEGORIES.find((c) => c.key === listing.category);
  const Icon = cat?.icon;
  const typeLabel = TYPE_LABELS[listing.listing_type]?.[lang] ?? listing.listing_type;
  const canAdminDelete = user?.is_admin;

  const handleAdminDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Ištrinti skelbimą „${listing.title}"?`)) return;
    try {
      await api.delete(`/listings/${listing.id}`);
      toast.success("Skelbimas ištrintas");
      onDeleted?.(listing.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Klaida trinant");
    }
  };

  const tagClass = {
    sell: "tag-sell",
    free: "tag-free",
    trade: "tag-trade",
    auction: "tag-auction",
  }[listing.listing_type] || "tag-sell";

  const priceLabel = listing.listing_type === "free"
    ? t("free")
    : listing.listing_type === "auction"
      ? `${(listing.highest_bid ?? listing.price).toFixed(2)} €`
      : `${listing.price.toFixed(2)} €`;

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="card-surface block overflow-hidden group"
    >
      <div className="aspect-[4/3] bg-[#F0EFEB] relative overflow-hidden">
        {listing.images && listing.images.length > 0 ? (
          <img
            src={fileUrl(listing.images[0])}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#7A7A73]">
            {Icon && <Icon className="w-12 h-12" />}
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5 items-center">
          <span className={`tag ${tagClass}`}>
            {listing.listing_type === "auction" && <Gavel className="w-3 h-3 mr-1"/>}
            {typeLabel}
          </span>
        </div>
        {listing.images && listing.images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
            <Camera className="w-3 h-3"/>
            {listing.images.length}
          </div>
        )}
        {canAdminDelete && (
          <button
            type="button"
            onClick={handleAdminDelete}
            data-testid={`admin-delete-${listing.id}`}
            title="Admin: ištrinti šį skelbimą"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading font-bold text-base text-[#1A1A18] line-clamp-1 group-hover:text-[#C85A32] transition-colors">
          {listing.title}
        </h3>
        <div className="mt-2 flex items-baseline justify-between gap-2">
          <span className="font-heading font-black text-xl text-[#1A1A18]">{priceLabel}</span>
          <span className="text-xs text-[#7A7A73] flex items-center gap-1">
            <MapPin className="w-3 h-3"/>{listing.city}
          </span>
        </div>
      </div>
    </Link>
  );
}
